import { useState } from 'react';
import { Calendar as CalendarIcon, X } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Button } from './button';
import { Calendar } from './calendar';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { cn } from '@/shared/lib/cn';

/**
 * shadcn-style single-date picker. Value is a `yyyy-mm-dd` string so it
 * stays trivially serializable and matches the legacy `<input type=date>`
 * contract the callers already use.
 *
 * Works inside another popover without clipping: the calendar portals
 * to the document body via Radix's PopoverPortal.
 */
export function DatePicker({
  value,
  onChange,
  placeholder = 'Seleccionar fecha',
  min,
  max,
  className,
  ariaLabel,
  clearable = true,
  disabled = false,
}: {
  /** `yyyy-mm-dd` string, or '' for no selection. */
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  /** Lower bound as `yyyy-mm-dd`. */
  min?: string;
  /** Upper bound as `yyyy-mm-dd`. */
  max?: string;
  className?: string;
  ariaLabel?: string;
  clearable?: boolean;
  /** When true, the trigger and clear affordance are inert. */
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const selected = toDate(value);
  const minDate = toDate(min);
  const maxDate = toDate(max);

  return (
    <Popover open={open} onOpenChange={(next) => !disabled && setOpen(next)}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          aria-label={ariaLabel}
          disabled={disabled}
          className={cn(
            'h-10 w-full justify-start gap-2 border-transparent bg-secondary px-3 font-normal hover:bg-secondary/80',
            !selected && 'text-muted-foreground',
            disabled && 'cursor-not-allowed opacity-50',
            className,
          )}
        >
          <CalendarIcon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
          <span className="truncate text-left">
            {selected ? format(selected, 'dd/MM/yyyy', { locale: es }) : placeholder}
          </span>
          {clearable && selected && !disabled && (
            <span
              role="button"
              tabIndex={0}
              aria-label="Limpiar fecha"
              className="ml-auto inline-flex h-5 w-5 items-center justify-center rounded hover:bg-background"
              onClick={(e) => {
                e.stopPropagation();
                onChange('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  onChange('');
                }
              }}
            >
              <X className="h-3 w-3 text-muted-foreground" aria-hidden />
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0">
        <Calendar
          mode="single"
          selected={selected ?? undefined}
          defaultMonth={selected ?? undefined}
          onSelect={(d) => {
            onChange(d ? toIsoString(d) : '');
            if (d) setOpen(false);
          }}
          disabled={(day) => {
            if (minDate && day < minDate) return true;
            if (maxDate && day > maxDate) return true;
            return false;
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

function toDate(iso: string | undefined): Date | null {
  if (!iso) return null;
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const [, y, mo, d] = m;
  return new Date(Number(y), Number(mo) - 1, Number(d));
}

function toIsoString(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
